import type CrateGameObj from "../entities/Crate";
import type PlayerBulletGO from "../entities/PlayerBullet";
import type MeleeEnemyEntity from "../entities/MeleeEnemy";
import { Dir } from "../entities/Player";
import type DoorEntity from "../entities/DoorEntity";
import ExtendedBlockEntity from "../entities/ExtendedBlockEntity";

export enum AiType {
  NaiveChase,
}

export enum EntityKind {
  Empty = "Empty", // This now means "nothing" (falling)

  // Game Entities with visual rep.
  Crate = "Crate",
  StaticWall = "SWall",
  PlayerBullet = "PlayerBullet",
  PressurePlate = "PressurePlate",
  LazorToggle = "LazorToggle",
  Door = "Door",
  MeleeEnemy = "MeleeEnemy",
  ExtendedBlock = "ExtendedBlock",

  // Entities used for logic with no 1:1 representation.
  PlayerGun = "T_PlayerGun",
  PlayerBody = "PlayerBody",
  TDamage = "Damage",
  WinLevel = "WinLevel",
}

export interface TDamage {
  kind: EntityKind.TDamage;
  id: number;
  x: number;
  y: number;
  turns_left: number;
}

export interface WinLevel {
  kind: EntityKind.WinLevel;
  id: number;
  x: number;
  y: number;
  transitionToLevelKey: string;
  transitionHint: string | undefined;
  levelNumber: number | undefined; // Purely aesthetic
}

export interface Empty {
  kind: EntityKind.Empty;
  id: number;
  x: number;
  y: number;
}

export interface StaticWall {
  kind: EntityKind.StaticWall;
  id: number;
  x: number;
  y: number;
}

// The player has a rail gun that fires in straight lines.
// Leaves fire behind.
export interface PlayerBullet {
  kind: EntityKind.PlayerBullet;
  id: number;
  x: number;
  y: number;
  dir: Dir;
}

export interface MeleeEnemy {
  kind: EntityKind.MeleeEnemy;
  id: number;
  x: number;
  y: number;
  dir: Dir;
  aiType: AiType;
  exhaustedTurn: boolean;
  entity?: () => MeleeEnemyEntity;
}

export interface PlayerGun {
  kind: EntityKind.PlayerGun;
  id: number;
  x: number;
  y: number;
}

export interface PlayerBody {
  kind: EntityKind.PlayerBody;
  id: number;
  x: number;
  y: number;
  dir?: Dir; // Used for the editor
}

export interface Crate {
  kind: EntityKind.Crate;
  id: number;
  x: number;
  y: number;
  reflects: string; // Leave empty or do '/' or '\'
  explodes: boolean;
  breaks: boolean;
  entity?: () => CrateGameObj; // Function wrap required to work with immer.js
}

export interface ExtendedBlock {
  kind: EntityKind.ExtendedBlock;
  id: number;
  x: number;
  y: number;
  sharedBlockId: number; // This number combines blocks together.
  entity?: () => ExtendedBlockEntity; // Function wrap required to work with immer.js
}

export interface PressurePlate {
  kind: EntityKind.PressurePlate;
  id: number;
  x: number;
  y: number;
  // This emits an arbitrary id. Not related to the entity ids.
  triggerId: number;
}
export interface LazorToggle {
  kind: EntityKind.LazorToggle;
  id: number;
  x: number;
  y: number;
  isActive: boolean;
  dir: Dir;
  // This emits an arbitrary id. Not related to the entity ids.
  triggerId: number;
}

export interface Door {
  kind: EntityKind.Door;
  id: number;
  x: number;
  y: number;
  triggerIdListener: number;
  isOpposite: boolean;
  isOpen: boolean;
  entity?: () => DoorEntity;
}

// Creating type safe patten matchers ref: https://medium.com/@fillopeter/pattern-matching-with-typescript-done-right-94049ddd671c
export type EntityType = Entities["kind"];
export type Entities =
  | StaticWall
  | Crate
  | PlayerBullet
  | PressurePlate
  | LazorToggle
  | Door
  | MeleeEnemy
  | PlayerGun
  | PlayerBody
  | TDamage
  | WinLevel
  | ExtendedBlock
  | Empty;

export type EntityMap<U> = {
  [K in EntityType]: U extends { kind: K } ? U : never;
};
export type EntityTypeMap = EntityMap<Entities>;
export type Pattern<T> = {
  [K in keyof EntityTypeMap]: (entity: EntityTypeMap[K]) => T;
};

// Example matcher: (In angle brackets we place return type)
export function matcher<T>(pattern: Pattern<T>): (entity: Entities) => T {
  return (entity) => pattern[entity.kind](entity as any);
}
